import { useRef, useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import {
  useActiveAccount,
  useActiveWallet,
  useActiveWalletChain,
  useSwitchActiveWalletChain,
} from "thirdweb/react";
import {
  createThirdwebClient,
  getContract,
  prepareContractCall,
  sendTransaction,
} from "thirdweb";
import { ThirdwebWalletConnect } from "../components/ThirdwebWalletConnect";
import { useNobodySausageGlyph } from "../contexts/NobodySausageGlyphContext";
import { useHttp } from "../hooks/useHttp";
import { logger } from "../utils/logger";
import {
  getNobodySausageImageUrl,
  NS_COLLECTION_NAME,
} from "../utils/nobodySausage";
import {
  NS_CHAIN,
  NS_CHAIN_ID,
  NS_CHAIN_NAME,
  NS_NATIVE_CURRENCY_SYMBOL,
} from "../config/chain";

const carouselCards = [
  {
    id: 1,
    image: "/images/nobodysausage/slider/1.png",
    title: "Tap the chip",
    description:
      "Hold your phone near the Nobody Sausage tag. A page will open, click <span class='font-medium text-black'>Claim now</span> or <span class='font-medium text-black'>Sign Up</span>.",
  },
  {
    id: 2,
    image: "/images/nobodysausage/slider/2.png",
    title: "Connect & confirm",
    description:
      "Choose Metamask, Glyph, or thirdweb login and connect it. When asked, <span class='font-medium text-black'>approve the connection</span>.",
  },
  {
    id: 3,
    image: "/images/nobodysausage/slider/3.png",
    title: "Claim your digital collectible",
    description:
      "The collectible preview will appear. Tap Claim and your Nobody Sausage collectible is now yours!",
  },
];

type DrawerType =
  | "loading"
  | "wallet_connect"
  | "scan_chip"
  | "scan_again"
  | "claim_nft"
  | "claiming"
  | "success"
  | "already_claimed"
  | "error"
  | null;

interface NobodySausageClaimResponse {
  success: boolean;
  action: "claim" | "already_claimed" | "wallet_not_connected";
  uid?: string;
  timestamp?: number;
  tokenType?: number;
  signature?: string;
  message: string;
}

function serializeError(err: any) {
  if (!err) return null;

  return {
    name: err.name,
    message: err.message,
    code: err.code,
    status: err.status,
    cause:
      err.cause && typeof err.cause === "object"
        ? {
            name: err.cause.name,
            message: err.cause.message,
            code: err.cause.code,
          }
        : err.cause,
    details: err.details,
    shortMessage: err.shortMessage,
    userOpHash: err.userOpHash,
    digest: err.digest,
    stack: err.stack,
  };
}

function getFriendlyClaimError(err: unknown): string {
  const message =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : "Failed to claim Nobody Sausage NFT";
  const normalized = message.toLowerCase();

  if (
    normalized.includes("user rejected") ||
    normalized.includes("user denied") ||
    normalized.includes("user cancelled") ||
    normalized.includes("action_rejected")
  ) {
    return "Transaction cancelled. Please try again.";
  }

  if (
    normalized.includes("requestdetails") ||
    normalized.includes("requestversion") ||
    normalized.includes("viem@") ||
    normalized.includes("signmessage")
  ) {
    return "Glyph could not complete the sponsored transaction. Please try again or use MetaMask/thirdweb login for claiming.";
  }

  if (
    normalized.includes("insufficient funds") ||
    normalized.includes("funds for gas")
  ) {
    return `This wallet does not have enough ${NS_NATIVE_CURRENCY_SYMBOL} to complete the transaction.`;
  }

  if (
    normalized.includes("switch to the target network") ||
    normalized.includes("chain") ||
    normalized.includes("network")
  ) {
    return `Please switch to ${NS_CHAIN_NAME} and try again.`;
  }

  if (
    normalized.includes("contract address not configured") ||
    normalized.includes("missing claim parameters")
  ) {
    return "Claim data is incomplete. Please refresh the page and try again.";
  }

  if (
    normalized.includes("failed to fetch") ||
    normalized.includes("networkerror") ||
    normalized.includes("network request")
  ) {
    return "Network error while claiming. Please check your connection and try again.";
  }

  return "We couldn't complete your claim right now. Please try again.";
}

export const ClaimNobodySausage = () => {
  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sendRequest } = useHttp();

  const account = useActiveAccount();
  const wallet = useActiveWallet();
  const { isConnected: isGlyphConnected } = useNobodySausageGlyph();
  const address = account?.address;
  const eoaAccount = useMemo(() => {
    return (
      (wallet as any)?.getAdminAccount?.() ||
      (wallet as any)?.getAccount?.() ||
      account
    );
  }, [wallet, account]);
  const eoaAddress = eoaAccount?.address;
  const activeWalletChain = useActiveWalletChain();
  const switchActiveWalletChain = useSwitchActiveWalletChain();

  const uid = searchParams.get("uid");
  const hasNfcParams = !!uid;

  const [currentDrawer, setCurrentDrawer] = useState<DrawerType>(null);
  const [claimResponse, setClaimResponse] =
    useState<NobodySausageClaimResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isSwitchingChain, setIsSwitchingChain] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);

  const contractAddress = process.env.REACT_APP_NS_CONTRACT_ADDRESS;
  const requiredChain = NS_CHAIN;

  const settings = {
    dots: false,
    infinite: false,
    speed: 450,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    arrows: false,
    afterChange: (current: number) => {
      setCurrentSlide(current);
    },
  };

  useEffect(() => {
    let isMounted = true;

    const validateAndCallAPI = async () => {
      if (!uid) {
        logger.ui("Nobody Sausage claim page loaded without UID parameter");
        return;
      }

      if (!/^[A-F0-9]+$/i.test(uid)) {
        if (isMounted) {
          setError("Invalid UID format");
          setCurrentDrawer("error");
        }
        return;
      }

      if (!address) {
        if (isMounted) {
          setCurrentDrawer("wallet_connect");
        }
        return;
      }

      if (!isMounted) return;
      setCurrentDrawer("loading");
      setError(null);

      try {
        const addressForSigning = eoaAddress || address;
        const { data } = await sendRequest<NobodySausageClaimResponse>({
          endpoint: `/api/nobodysausage_claim?uid=${encodeURIComponent(
            uid as string
          )}${addressForSigning ? `&address=${encodeURIComponent(addressForSigning)}` : ""}`,
          method: "GET",
          responseType: "json",
        });

        if (!isMounted) return;
        setClaimResponse(data);

        if (!data.success) {
          setError(data.message);
          if (data.action === "already_claimed") {
            setCurrentDrawer("already_claimed");
          } else {
            setCurrentDrawer("error");
          }
          return;
        }

        switch (data.action) {
          case "claim":
            setCurrentDrawer("claim_nft");
            break;
          case "wallet_not_connected":
            setCurrentDrawer("wallet_connect");
            break;
          case "already_claimed":
            setError(
              data.message || "This Nobody Sausage NFT has already been claimed"
            );
            setCurrentDrawer("already_claimed");
            break;
          default:
            setError("Unknown response from server");
            setCurrentDrawer("error");
        }
      } catch (err: any) {
        if (!isMounted) return;
        if (err?.name === "AbortError" || err?.message?.includes("abort")) {
          return;
        }
        logger.apiError("Nobody Sausage claim API call error", err as Error);
        setError(
          err instanceof Error ? err.message : "Failed to validate UID"
        );
        setCurrentDrawer("error");
      }
    };

    validateAndCallAPI();

    return () => {
      isMounted = false;
    };
  }, [uid, address, eoaAddress, sendRequest]);

  useEffect(() => {
    if (address && currentDrawer === "wallet_connect" && !hasNfcParams) {
      setCurrentDrawer("scan_again");
    }
  }, [address, currentDrawer, hasNfcParams]);

  useEffect(() => {
    if (address && currentDrawer === "wallet_connect" && uid) {
      const retryCall = async () => {
        setCurrentDrawer("loading");
        setError(null);

        try {
          const addressForSigning = eoaAddress || address;
          const { data } = await sendRequest<NobodySausageClaimResponse>({
            endpoint: `/api/nobodysausage_claim?uid=${encodeURIComponent(
              uid as string
            )}&address=${encodeURIComponent(addressForSigning)}`,
            method: "GET",
            responseType: "json",
          });

          setClaimResponse(data);

          if (!data.success) {
            setError(data.message);
            if (data.action === "already_claimed") {
              setCurrentDrawer("already_claimed");
            } else {
              setCurrentDrawer("error");
            }
            return;
          }

          if (data.action === "claim") {
            setCurrentDrawer("claim_nft");
          } else if (data.action === "already_claimed") {
            setError(data.message || "Already claimed");
            setCurrentDrawer("already_claimed");
          }
        } catch (err: any) {
          logger.apiError("Nobody Sausage claim API retry error", err as Error);
          setError(
            err instanceof Error ? err.message : "Failed to validate UID"
          );
          setCurrentDrawer("error");
        }
      };

      retryCall();
    }
  }, [address, eoaAddress, currentDrawer, uid, sendRequest]);

  useEffect(() => {
    if (currentDrawer) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [currentDrawer]);

  useEffect(() => {
    const autoSwitch = async () => {
      if (
        address &&
        activeWalletChain &&
        activeWalletChain.id !== NS_CHAIN_ID &&
        switchActiveWalletChain &&
        !isSwitchingChain
      ) {
        try {
          setIsSwitchingChain(true);
          await switchActiveWalletChain(requiredChain);
        } catch {
          // user can switch manually
        } finally {
          setIsSwitchingChain(false);
        }
      }
    };

    const timeoutId = setTimeout(() => {
      autoSwitch();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [
    address,
    activeWalletChain,
    switchActiveWalletChain,
    requiredChain,
    isSwitchingChain,
  ]);

  const handleSwitchNetwork = async () => {
    if (!switchActiveWalletChain) return;
    setIsSwitchingChain(true);
    setError(null);

    try {
      await switchActiveWalletChain(requiredChain);
      setError(null);
    } catch (err: any) {
      if (
        err?.code === 4001 ||
        err?.code === "ACTION_REJECTED" ||
        err?.message?.toLowerCase().includes("user rejected") ||
        err?.message?.toLowerCase().includes("user denied") ||
        err?.message?.toLowerCase().includes("user cancelled")
      ) {
        setError(
          `Network switch cancelled. Please switch to ${NS_CHAIN_NAME} to continue.`
        );
      } else {
        setError(
          "Failed to switch network. Please switch manually in your wallet."
        );
      }
    } finally {
      setIsSwitchingChain(false);
    }
  };

  const handleClaimNFT = async () => {
    if (
      !address ||
      !account ||
      !claimResponse ||
      claimResponse.action !== "claim"
    ) {
      return;
    }

    const { uid: claimUid, timestamp, tokenType, signature } = claimResponse;
    const caller = eoaAccount?.address || address;
    const accountAddress = account.address;
    const isGlyphWallet =
      isGlyphConnected ||
      !wallet?.id ||
      String(wallet.id).toLowerCase().includes("glyph");
    const claimStartedAt = Date.now();

    if (!claimUid || signature == null || timestamp == null || tokenType == null) {
      setError("Missing claim parameters");
      logger.contract("Nobody Sausage claim aborted: missing claim parameters", {
        claimUid,
        timestamp,
        tokenType,
        hasSignature: signature != null,
      });
      return;
    }

    if (
      isGlyphConnected &&
      caller &&
      accountAddress.toLowerCase() === caller.toLowerCase()
    ) {
      setError("Preparing your sponsored Glyph account. Please try again in a moment.");
      setCurrentDrawer("claim_nft");
      logger.wallet("Nobody Sausage claim blocked: Glyph smart account not ready", {
        walletId: wallet?.id,
        accountAddress,
        caller,
        activeWalletChainId: activeWalletChain?.id || 0,
        requiredChainId: NS_CHAIN.id,
      });
      return;
    }

    if ((activeWalletChain?.id || 0) !== NS_CHAIN.id) {
      setError("Please switch to the target network");
      logger.wallet("Nobody Sausage claim blocked: wallet on wrong chain", {
        activeWalletChainId: activeWalletChain?.id || 0,
        requiredChainId: NS_CHAIN.id,
        walletId: wallet?.id,
        address,
        caller,
      });
      return;
    }

    setIsClaiming(true);
    setCurrentDrawer("claiming");
    setError(null);

    try {
      logger.contract("Nobody Sausage claim started", {
        uid: claimUid,
        address,
        accountAddress,
        caller,
        contractAddress,
        walletId: wallet?.id,
        isGlyphWallet,
        isGlyphConnected,
        activeWalletChainId: activeWalletChain?.id || 0,
        requiredChainId: NS_CHAIN.id,
        timestamp,
        tokenType,
        signatureLength: signature.length,
      });

      const response = await fetch("/NobodySausage223NFTAbi.json");
      const abi = await response.json();

      if (!contractAddress) {
        throw new Error("Contract address not configured");
      }

      const client = createThirdwebClient({
        clientId: process.env.REACT_APP_THIRDWEB_CLIENT_ID as string,
      });
      const contract = getContract({
        client,
        chain: requiredChain,
        address: contractAddress,
        abi,
      });

      const transaction = prepareContractCall({
        contract,
        method: "claim",
        params: [timestamp, tokenType, caller, claimUid, signature],
      });

      logger.contract("Nobody Sausage claim transaction prepared", {
        uid: claimUid,
        accountAddress,
        caller,
        contractAddress,
        method: "claim",
      });

      logger.contract("Nobody Sausage claim sending transaction", {
        uid: claimUid,
        walletId: wallet?.id,
        accountAddress,
        caller,
        activeWalletChainId: activeWalletChain?.id || 0,
        requiredChainId: NS_CHAIN.id,
      });

      const receipt = await sendTransaction({
        account,
        transaction,
      });

      logger.contract("Nobody Sausage claim sendTransaction resolved", {
        uid: claimUid,
        elapsedMs: Date.now() - claimStartedAt,
        receipt: receipt as any,
      });

      const txHashResult = (receipt as any)?.transactionHash;
      setTxHash(txHashResult || null);

      if (txHashResult) {
        logger.contract("Nobody Sausage claim mined", {
          uid: claimUid,
          transactionHash: txHashResult,
          elapsedMs: Date.now() - claimStartedAt,
        });
        setCurrentDrawer("success");
      } else {
        throw new Error("Transaction failed");
      }
    } catch (err: any) {
      logger.contract("Nobody Sausage claim failed with raw error", {
        uid: claimResponse?.uid,
        elapsedMs: Date.now() - claimStartedAt,
        walletId: wallet?.id,
        isGlyphWallet,
        isGlyphConnected,
        activeWalletChainId: activeWalletChain?.id || 0,
        accountAddress,
        caller,
        error: serializeError(err),
      });

      logger.contractError("Failed to claim Nobody Sausage NFT", err as Error, {
        uid: claimResponse?.uid,
        address,
        contractAddress,
      });

      if (
        err?.code === 4001 ||
        err?.code === "ACTION_REJECTED" ||
        err?.message?.toLowerCase().includes("user rejected") ||
        err?.message?.toLowerCase().includes("user denied") ||
        err?.message?.toLowerCase().includes("user cancelled")
      ) {
        setError("Transaction cancelled. Please try again.");
        setCurrentDrawer("claim_nft");
      } else {
        setError(getFriendlyClaimError(err));
        setCurrentDrawer("error");
      }
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <div className="py-5">
      <div className="px-6 mb-8">
        {!uid && (
          <div className="py-[22px] rounded-[12px] bg-gradient-to-r from-[#3FA3FF] to-[#FF5A7F] flex flex-col items-center justify-center gap-2">
            <div className="font-bold text-center text-white">
              Sign up for 223 and Claim <br /> Nobody Sausage NFTs in a Flash
            </div>

            <button
              onClick={() => setCurrentDrawer("wallet_connect")}
              className="bg-[#343434] h-10 text-white font-bold rounded-[50px] px-10 hover:bg-[#4a4a4a] transition-colors cursor-pointer"
            >
              Claim Now
            </button>
          </div>
        )}

        {uid && (
          <div className="rounded-[22px] overflow-hidden aspect-square p-3 relative mb-4">
            <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 animate-pulse" />
            <img
              src={getNobodySausageImageUrl(undefined)}
              alt="Nobody Sausage"
              className="object-cover absolute inset-0 w-full h-full"
            />
          </div>
        )}
      </div>

      <div className="px-6">
        <h3 className="mb-4 text-2xl font-bold text-center">How it Works</h3>

        <div className="relative carousel-container">
          <Slider ref={sliderRef} {...settings}>
            {carouselCards.map((card) => (
              <CarouselCard key={card.id} card={card} />
            ))}
          </Slider>
        </div>

        <div className="my-3 text-center">
          <h4 className="mb-2 text-xl font-bold">
            {carouselCards[currentSlide].title}
          </h4>

          <p
            className="text-lg leading-tight text-[#626262] whitespace-pre-line"
            dangerouslySetInnerHTML={{
              __html: carouselCards[currentSlide].description,
            }}
          ></p>
        </div>

        <div className="flex gap-2 justify-center items-center mt-5 mb-6">
          {carouselCards.map((_, index) => (
            <div
              key={index}
              className={`rounded-full cursor-pointer transition-all ${
                currentSlide === index
                  ? "w-[37px] h-[10px] bg-[#000000]"
                  : "w-[10px] h-[10px] bg-[#CCCCCC]"
              }`}
              onClick={() => sliderRef.current?.slickGoTo(index)}
            />
          ))}
        </div>
      </div>

      {currentDrawer === "loading" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <div className="flex justify-center items-center w-16 h-16 bg-blue-100 rounded-full animate-spin">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>

            <div className="text-center space-y-2.5 mt-8">
              <div className="text-2xl font-bold text-black">
                Validating UID
              </div>
              <div className="text-[#626262]">
                Please wait while we verify your Nobody Sausage...
              </div>
            </div>
          </div>
        </div>
      )}

      {currentDrawer === "wallet_connect" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center relative">
            <button
              type="button"
              onClick={() => setCurrentDrawer(null)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="mb-4">
              <img src="/svg/223-logo.svg" alt="223 Logo" />
            </div>

            <div className="text-center space-y-2.5 w-full">
              <div className="text-2xl font-bold text-black">Sign In</div>

              <div className="text-[#626262]">
                To claim your 223 Digital Collectible, sign in or create an
                account
              </div>

              <div className="!mt-6 flex justify-center">
                <ThirdwebWalletConnect showDropdown={false} />
              </div>
            </div>
          </div>
        </div>
      )}

      {currentDrawer === "scan_chip" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img src="/svg/scan-chip.svg" alt="scan-chip" />

            <div className="mt-8 text-center">
              <div className="text-2xl font-bold text-black">
                Please scan the NFC chip
              </div>
              <div className="text-[#626262] mt-2">
                Tap your phone near the Nobody Sausage tag to continue
              </div>
            </div>
          </div>
        </div>
      )}

      {currentDrawer === "scan_again" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-12 max-h-fit flex justify-center flex-col items-center">
            <img src="/images/nobodysausage/wallet-connected.png" alt="wallet-connected" className="mx-auto" />

            <div className="text-center space-y-2.5 mt-6">
              <div className="text-2xl font-bold text-black">
                Connected Successfully!
              </div>

              <div className="text-[#626262] leading-relaxed">
                Wallet connected successfully! Please{" "}
                <span className="font-semibold text-black">close this tab</span>{" "}
                and tap the NFC chip to complete your claim.
              </div>

              <button
                onClick={() => setCurrentDrawer(null)}
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 !mt-2"
              >
                Close and tap again
              </button>
            </div>
          </div>
        </div>
      )}

      {currentDrawer === "claim_nft" && claimResponse && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit">
            <div className="space-y-2.5">
              <div className="rounded-[22px] overflow-hidden aspect-square p-3 relative mx-auto">
                <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400" />
                <img
                  src={getNobodySausageImageUrl(claimResponse.tokenType)}
                  alt="Nobody Sausage"
                  className="object-cover absolute inset-0 w-full h-full"
                />

                <div className="backdrop-blur-md rounded-[19px] py-4 absolute bottom-3 right-3 left-3 bg-white/10">
                  <div className="max-w-[240px] mx-auto flex flex-col items-center justify-center gap-2">
                    <h3 className="text-lg font-bold text-center">
                      {NS_COLLECTION_NAME} Authenticity Certificate
                    </h3>

                    {(activeWalletChain?.id || 0) === NS_CHAIN_ID && (
                      <button
                        onClick={handleClaimNFT}
                        disabled={isClaiming}
                        className="bg-[#343434] h-10 text-white font-bold rounded-[50px] px-10 disabled:opacity-50"
                      >
                        Claim Now
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {error && (
                <div className="px-4 py-3 text-sm text-red-700 bg-red-100 rounded border border-red-400">
                  {error}
                </div>
              )}

              {(activeWalletChain?.id || 0) !== NS_CHAIN_ID && (
                <div>
                  <div className="px-4 py-3 mb-2 text-sm text-yellow-700 bg-yellow-100 rounded border border-yellow-400">
                    Please switch to the target network
                  </div>

                  <button
                    onClick={handleSwitchNetwork}
                    disabled={isSwitchingChain}
                    className="py-2 w-full text-lg font-bold text-white bg-orange-600 transition-colors duration-200 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSwitchingChain ? "Switching..." : "Switch Network"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {currentDrawer === "claiming" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img src="/images/nobodysausage/wallet-connected.png" alt="claiming" className="" />

            <div className="text-center space-y-2.5 mt-8">
              <div className="text-2xl font-bold text-black">Claiming</div>

              <div className="text-[#626262]">
                Claiming your Digital Collectible. Please wait.
              </div>

              {txHash && (
                <div className="px-4 text-xs text-gray-500 break-all">
                  Transaction:{" "}
                  <a
                    href={`https://snowtrace.io/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {txHash.slice(0, 10)}...{txHash.slice(-8)}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {currentDrawer === "success" && claimResponse && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <div className="space-y-2 text-center">
              <div className="text-lg font-bold text-center">🎉 CONGRATS!</div>

              <div className="text-[#626262]">
                Congratulations! Your Nobody Sausage Digital Collectible has been
                claimed.
              </div>

              {txHash && (
                <div className="px-4 text-xs text-gray-500 break-all">
                  Transaction:{" "}
                  <a
                    href={`https://snowtrace.io/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    {txHash.slice(0, 10)}...{txHash.slice(-8)}
                  </a>
                </div>
              )}

              <div className="rounded-[22px] overflow-hidden aspect-square p-3 relative mx-auto">
                <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400" />
                <img
                  src={getNobodySausageImageUrl(claimResponse.tokenType)}
                  alt="Nobody Sausage"
                  className="object-cover absolute inset-0 w-full h-full"
                />
              </div>

              <div className="text-[#626262]">Certificate of Authenticity</div>

              <button
                onClick={() => navigate("/rewards/nobodysausage")}
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 !mt-6"
              >
                Go to profile
              </button>
            </div>
          </div>
        </div>
      )}

      {currentDrawer === "already_claimed" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit">
            <div className="text-center">
              <img
                src="/images/nobodysausage/wallet-connected.png"
                alt="wallet-connected"
                className="mx-auto"
              />

              <h2 className="mt-2 text-xl font-bold">Already Claimed</h2>

              <p className="mb-6 text-gray-600">
                {error || "This Nobody Sausage NFT has already been claimed"}
              </p>

              <button
                onClick={() => navigate("/rewards/nobodysausage")}
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800"
              >
                Go to profile
              </button>
            </div>
          </div>
        </div>
      )}

      {currentDrawer === "error" && (
        <div className="flex fixed inset-0 z-50 items-end h-dvh bg-black/70">
          <div className="w-full bg-white rounded-t-[32px] p-6 pb-10 max-h-fit flex justify-center flex-col items-center">
            <img
              src="/images/nobodysausage/error.png"
              alt="error"
              className="mb-4"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />

            <div className="text-center space-y-2.5">
              <div className="text-2xl font-bold text-black">
                Something Went Wrong
              </div>

              <div className="text-[#626262]">
                {error || "An unexpected error occurred. Please try again."}
              </div>

              <button
                onClick={() =>
                  window.open("/support", "_blank", "noopener,noreferrer")
                }
                className="py-2 w-full font-bold text-lg text-white bg-[#1F1F1F] transition-colors duration-200 hover:bg-gray-800 !mt-6"
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CarouselCard = ({
  card,
}: {
  card: { id: number; image: string; title: string; description: string };
}) => {
  return (
    <div className="w-full rounded-[14px] overflow-hidden bg-gradient-to-br from-purple-400 via-purple-500 to-purple-600 shadow-xl mx-auto">
      <div className="relative w-full h-full rounded-[14px] overflow-hidden aspect-[3/4]">
        <img
          src={card.image}
          alt={card.title}
          className="object-cover absolute inset-0 w-full h-full"
        />
      </div>
    </div>
  );
};
